import { capitalize } from 'lodash';
import { useMemo } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import CheckboxField from 'dashboard/components/FormControls/CheckboxField';
import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import {
  CaseState,
  CaseStateColor,
  CaseStateColorSelectableArray,
} from 'dashboard/models/case-template';
import CaseStatusColorContainer from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable/CaseStatusColorContainer';
import CaseStatusColorOption from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable/CaseStatusColorOption';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { isRequired, withMessage } from 'lib/utils/formValidation';
import isUnique from 'lib/utils/formValidation/isUnique';

interface FormAddCaseStatusProps {
  allStates: CaseState[];
  isTheOnlyValidInitialState: boolean;
  onClose: () => void;
}

const CASE_STATUS_COLOR_OPTIONS = CaseStateColorSelectableArray.map((color: CaseStateColor) => ({
  label: capitalize(color),
  value: color,
}));

const FormAddCaseStatus = ({
  allStates,
  isTheOnlyValidInitialState,
  submitting,
  pristine,
  handleSubmit,
  onClose,
}: FormAddCaseStatusProps & InjectedFormProps<FormAddCaseStatusData, FormAddCaseStatusProps>) => {
  const validate = useMemo(() => {
    const allStatusNames = allStates.map((status: CaseState) => status.name);
    return {
      name: [
        withMessage('Please enter a status name.', isRequired),
        withMessage(
          'Status name already exists. Please choose a different name.',
          isUnique(allStatusNames),
        ),
      ],
      color: [withMessage('Please select a color for this status.', isRequired)],
    };
  }, [allStates]);

  return (
    <Form
      data-test='add-case-status-form'
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(e);
        e.stopPropagation();
      }}
    >
      <FormSection>
        <Field
          label='Name'
          name='name'
          disabled={submitting}
          type='text'
          component={InputField}
          data-test='add-case-status-form__name'
          validate={validate.name}
        />
        <Field
          label='End state'
          name='end-state'
          disabled={submitting || isTheOnlyValidInitialState}
          type='checkbox'
          component={CheckboxField}
          data-test='add-case-status-form__end-state'
        />
        <Spacer height={1} />
        <Field
          label='Require comment'
          name='comment-required'
          disabled={submitting}
          type='checkbox'
          component={CheckboxField}
        />
      </FormSection>
      <Spacer height={1} />
      <Field
        label='Color'
        name='color'
        disabled={submitting}
        options={CASE_STATUS_COLOR_OPTIONS}
        components={{ Option: CaseStatusColorOption, SingleValue: CaseStatusColorContainer }}
        component={ReactSelectField}
        data-test='add-case-status-form__color'
        validate={validate.color}
        portal
      />
      <HStack equal={true}>
        <Button
          id='add-case-status-form__close'
          name='add-case-status-form__close'
          variant='basic'
          onClick={onClose}
          data-test='add-case-status-form__close'
        >
          Cancel
        </Button>
        <Button
          id='add-case-status-form__submit'
          name='add-case-status-form__submit'
          type='submit'
          disabled={pristine || submitting}
          value='Submit'
          data-test='add-case-status-form__submit'
        >
          Save
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormAddCaseStatusData {
  name: string;
  color: CaseStateColor;
  'end-state': boolean;
  'comment-required': boolean;
}

export default reduxForm<FormAddCaseStatusData, FormAddCaseStatusProps>({
  form: 'addCaseStatus',
})(FormAddCaseStatus);
